<template>
  <div class="series-statistics-tab" v-loading="loading">
    <origin-data
      :hits="tableData"
      :byCourse="false"
      :show-filter="false"
      :page.sync="pageInfo.page"
      :size.sync="pageInfo.size"
      :total="pageInfo.total"
      :show-pagination="true"
      :tableColumns="tableColumns"
      tapRowAction="article"
      @changePageInfo="getListHandle()"
    />
  </div>
</template>

<script>
import EventBus from '@/lib/eventBus';
import { fetchStatisticValKey, fetchFieldsQuery, getRowDefaultCategoryMode } from '@/lib/utils';
import { services } from '@/service';
import { uniqBy } from 'lodash';
import dayjs from 'dayjs';
import originData from '@/components/originData';
import { emptyColumnText, caseNumber, caseBaseTitle, sysCaseWays } from '@/constant';

export default {
  components: { originData },
  data() {
    return {
      pageInfo: {
        page: 1,
        size: 20,
        total: 0
      },
      loading: false,
      tableColumns: [],
      tableData: []
    };
  },
  props: {
    series: {
      type: Object,
      required: true
    },
    rows: {
      type: Array,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    statistics: {
      type: Array,
      required: true
    },
    relationFilterVal: {
      type: String,
      required: true
    },
    instrumentType: {
      type: String,
      required: true
    }
  },
  inject: {
    courseUnitGroups: { default: () => [] }
  },
  computed: {
    curUnitGroup() {
      return this.courseUnitGroups.find(groupItem => groupItem.id === this.relationFilterVal);
    },
    statisticInfo() {
      const { rows, statistics } = this;
      let seriesName = this.series.seriesName;
      let type = 'label';

      if (this.series.seriesType === 'pie') {
        seriesName = this.series.name;
      }

      if (rows.length < 1) {
        if (statistics.length === 1) {
          const statisticItem = statistics[0];
          if (statisticItem.value === 'row_count') {
            type = `有${statisticItem.fieldDetail.title}` === seriesName ? 'label' : 'none';
          }
          return { type, item: statistics[0] };
        }
        if (statistics.length > 1) {
          return { type, item: statistics.find(item => item.fieldDetail.title === this.series.name) };
        }
        return {
          type,
          item: statistics.find(
            item => fetchStatisticValKey(item, { statisticName: this.curUnitGroup.name }) === seriesName
          )
        };
      }

      if (rows.length === 1) {
        return {
          type,
          item: statistics.find(
            item => fetchStatisticValKey(item, { statisticName: this.curUnitGroup.name }) === seriesName
          )
        };
      }

      return { type, item: statistics[0] };
    }
  },
  mounted() {
    this.refreshHandle();
  },
  methods: {
    async refreshHandle() {
      this.pageInfo = { size: 20, page: 1, total: 0 };
      this.initTableColumns();
      await this.getListHandle();
    },
    async getListHandle() {
      try {
        this.loading = true;
        const { page, size } = this.pageInfo;
        let queryBody = await new Promise(resolve => {
          EventBus.$emit('fetchQueryBody', params => resolve(params));
        });
        const fields = this.tableColumns.map(fieldItem => fetchFieldsQuery(fieldItem));
        queryBody = {
          ...queryBody,
          fields,
          size: size,
          from: (page - 1) * size,
          multiple: true
        };
        queryBody.query = this.formatQuery([...queryBody.query]);

        const result = await services.originCase.getCaseLabelList(queryBody);
        this.tableData = result.hits;
        this.pageInfo.total = result.total;
      } catch (err) {
        this.$error(err);
      } finally {
        this.loading = false;
      }
    },
    // 添加筛选值，包括具体的筛选条件 及 某个标签需要存在标注
    formatQuery(query) {
      if (this.statisticInfo.item && !this.statisticInfo.item.fieldDetail.isDefaultStatistic) {
        query.push({
          isLabelFilter: true,
          info: {
            fieldDetail: this.statisticInfo.item.fieldDetail,
            type: this.statisticInfo.type
          }
        });
      }

      this.rows.forEach((rowItem, rowIndex) => {
        const seriesName = rowIndex === 0 ? this.series.name : this.series.seriesName;
        const newQuery = this.fetchRowQuery(rowItem, seriesName);
        newQuery && query.push(newQuery);
      });

      return query;
    },
    // 根据点击添加具体的筛选条件【例如文本匹配、日期和数值区间】
    fetchRowQuery(rowItem, seriesName) {
      if (seriesName === emptyColumnText) {
        const filterFieldArr = [
          {
            formData: {
              rule: 'exists_not'
            },
            columnInfo: rowItem
          }
        ];
        if (rowItem.type === 'text') {
          if (rowItem.deputy_type === 'dict') {
            filterFieldArr.push({
              formData: {
                rule: 'term',
                keywordMode: 'remote',
                keywordArr: [seriesName]
              },
              columnInfo: rowItem
            });
          } else {
            filterFieldArr.push({
              formData: {
                rule: 'term',
                keywordMode: 'input',
                keyword: seriesName
              },
              columnInfo: rowItem
            });
          }
        }
        return {
          fieldType: 'group',
          op_type: 'or',
          fieldArr: filterFieldArr
        };
      }

      if (rowItem.type === 'text') {
        if (getRowDefaultCategoryMode(rowItem)) {
          if (seriesName === '-') {
            return {
              isLabelFilter: true,
              info: {
                fieldDetail: rowItem,
                type: 'none'
              }
            };
          } else {
            if (rowItem.deputy_type === 'dict') {
              return {
                formData: {
                  rule: 'term',
                  keywordMode: 'remote',
                  keywordArr: [seriesName]
                },
                columnInfo: rowItem
              };
            } else {
              return {
                formData: {
                  rule: 'term',
                  keywordMode: 'input',
                  keyword: seriesName
                },
                columnInfo: rowItem
              };
            }
          }
        } else {
          return {
            isLabelFilter: true,
            info: {
              fieldDetail: rowItem,
              type: [`是${rowItem.title}`, `有${rowItem.title}`].includes(seriesName) ? 'label' : 'none'
            }
          };
        }
      }
      if (rowItem.type === 'num') {
        const numRange = seriesName.split('-');
        if (numRange.length === 1) {
          if (isNaN(numRange[0])) {
            return {
              isLabelFilter: true,
              info: {
                fieldDetail: rowItem,
                type: seriesName === '无' ? 'none' : 'label'
              }
            };
          }
          return {
            formData: {
              rule: 'term',
              num: numRange[0]
            },
            columnInfo: rowItem
          };
        }
        if (numRange[0] === undefined && numRange[1] === undefined) {
          return;
        }
        const valueOfFilter = JSON.parse(rowItem.valueOfFilter || '{}');
        let { type, fromEq, toEq } = valueOfFilter;
        const formData = {
          rule: 'range',
          numArr: [numRange[0], numRange[1]]
        };
        if (!type || type === 'auto') {
          formData.rule = 'lrange';
        }
        if (type === 'custom') {
          fromEq = fromEq || fromEq === undefined;
          toEq = !!toEq;
          if (fromEq && toEq) {
            formData.rule = 'erange';
          } else if (fromEq) {
            formData.rule = 'lrange';
          } else {
            formData.rule = 'rrange';
          }
        }
        return {
          formData,
          columnInfo: rowItem
        };
      }
      if (rowItem.type === 'date' && seriesName !== '-') {
        const valueOfFilter = JSON.parse(rowItem.valueOfFilter || '{}');
        const { fixedValue = 'year' } = valueOfFilter;
        if (fixedValue === 'year') {
          return {
            formData: {
              rule: 'range',
              date: [`${seriesName}-01-01`, `${seriesName}-12-31`]
            },
            columnInfo: rowItem
          };
        }
        if (fixedValue === 'month') {
          const firstDateVal = `${seriesName}-01`;
          const lastDateVal = dayjs(firstDateVal)
            .add(1, 'month')
            .subtract(1, 'day')
            .format('YYYY-MM-DD');
          return {
            formData: {
              rule: 'range',
              date: [firstDateVal, lastDateVal]
            },
            columnInfo: rowItem
          };
        }
        if (fixedValue === 'day') {
          return {
            formData: {
              rule: 'range',
              date: [`${seriesName}`, `${seriesName}`]
            },
            columnInfo: rowItem
          };
        }
      }
    },
    initTableColumns() {
      let tableColumns = [];
      
      if (sysCaseWays.includes(this.instrumentType)) {
        tableColumns.push({ ...caseNumber });
      } else {
        tableColumns.push({ ...caseBaseTitle });
      }

      if (this.relationFilterVal !== 'case') {
        tableColumns = [...tableColumns, ...(this.curUnitGroup.fields || [])];
      }

      const rowsLength = this.rows.length;
      [...this.rows, ...this.statistics].forEach((item, index) => {
        const detail = index < rowsLength ? item : item.fieldDetail;
        tableColumns.push(detail);
      });

      tableColumns = tableColumns.filter(columnInfo => !columnInfo.isDefaultStatistic);

      this.tableColumns = uniqBy(tableColumns, 'columnKey');
    }
  }
};
</script>

<style lang="scss" scoped>
.series-statistics-tab {
}
</style>
